import { useState, useEffect } from 'react';
import type { Group, GroupInput } from '../../api';
import { INPUT, SELECT, LABEL, BTN_PRIMARY, BTN_SECONDARY, TEXTAREA } from '../common/ui';


interface GroupFormProps {
  group?: Group | null;
  parentGroups: Group[];
  onSubmit: (data: GroupInput) => Promise<void> | void;
  onCancel: () => void;
}

export default function GroupForm({ group, parentGroups, onSubmit, onCancel }: GroupFormProps) {
  const [form, setForm] = useState<GroupInput>({ name: '', description: '', parent_id: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (group) {
      setForm({ name: group.name || '', description: group.description || '', parent_id: group.parent_id || null });
    }
  }, [group]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={LABEL}>Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          className={INPUT}
          placeholder="Group name"
        />
      </div>

      <div>
        <label className={LABEL}>Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className={TEXTAREA}
          placeholder="Optional description"
        />
      </div>

      <div>
        <label className={LABEL}>Parent Group</label>
        <select
          value={form.parent_id || ''}
          onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
          className={SELECT}
        >
          <option value="">None (root level)</option>
          {(parentGroups || [])
            .filter((g) => !group || g.id !== group.id)
            .map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className={BTN_SECONDARY}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !form.name}
          className={BTN_PRIMARY}
        >
          {loading ? 'Saving…' : group ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}
